import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
    Home,
    Calendar,
    Cart,
    Box,
    CheckCircle,
    ChatBubble,
    Microphone,
    LogOut,
    ChatBubbleQuestion,
    Settings,
    MoreVert,
    NavArrowDown,
    Plus,
    HomeSimpleDoor,
    Heart,
    LockSquare,
    Language,
    Sparks,
} from "iconoir-react";
import { useT } from "@/lib/i18n";
import {
    loadActiveHousehold,
    clearActiveHousehold,
    fetchHousehold,
    logoutApi,
    firstName,
    fetchMyHouseholds,
    saveActiveHousehold,
    confirmCheckoutSession,
    openBillingPortal,
    api,
} from "@/lib/api";
import { useAuth } from "@/lib/auth";
import TodayTab from "@/components/TodayTab";
import WeekTab from "@/components/WeekTab";
import ShoppingTab from "@/components/ShoppingTab";
import InventoryTab from "@/components/InventoryTab";
import ChoresTab from "@/components/ChoresTab";
import ChatTab from "@/components/ChatTab";
import VoiceOnboarding from "@/components/VoiceOnboarding";
import InviteDialog from "@/components/InviteDialog";
import FeedbackDialog from "@/components/FeedbackDialog";
import ReferFriendDialog from "@/components/ReferFriendDialog";
import SetPasswordDialog from "@/components/SetPasswordDialog";
import TelegramDialog from "@/components/TelegramDialog";
import { toast } from "sonner";

const TABS = [
    { id: "today", labelKey: "nav.today", icon: Home },
    { id: "week", labelKey: "nav.week", icon: Calendar },
    { id: "shopping", labelKey: "nav.shopping", icon: Cart },
    { id: "inventory", labelKey: "nav.inventory", icon: Box },
    { id: "chores", labelKey: "nav.chores", icon: CheckCircle },
    { id: "chat", labelKey: "nav.chat", icon: ChatBubble },
];

// Deterministic pastel avatar color from name
const AVATAR_COLORS = ["#F3D469", "#CFE4D0", "#FBE0DC", "#ECE4F5", "#FDE4B7", "#C6D9EB", "#E8D4C1"];
export function avatarColor(seed) {
    if (!seed) return AVATAR_COLORS[0];
    let h = 0;
    for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) | 0;
    return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length];
}
export function initials(name) {
    if (!name) return "·";
    const parts = name.trim().split(/\s+/);
    return ((parts[0]?.[0] || "") + (parts[1]?.[0] || "")).toUpperCase() || "·";
}

export default function MiloApp() {
    const nav = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { user, setUser, refresh: refreshAuth } = useAuth();
    const { t, lang, setLang } = useT();
    const [session, setSession] = useState(loadActiveHousehold());
    const [tab, setTab] = useState("today");
    const [members, setMembers] = useState([]);
    const [voiceOpen, setVoiceOpen] = useState(false);
    const [inviteOpen, setInviteOpen] = useState(false);
    const [feedbackOpen, setFeedbackOpen] = useState(false);
    const [referOpen, setReferOpen] = useState(false);
    const [passwordOpen, setPasswordOpen] = useState(false);
    const [telegramOpen, setTelegramOpen] = useState(false);
    const [refreshKey, setRefreshKey] = useState(0);
    const [menuOpen, setMenuOpen] = useState(false);
    const [householdMenu, setHouseholdMenu] = useState(false);
    const [myHouseholds, setMyHouseholds] = useState([]);

    // Open feedback modal directly from /app?feedback=1 (used by Day 14 lifecycle email)
    useEffect(() => {
        if (searchParams.get("feedback") === "1") {
            setFeedbackOpen(true);
            // Clean up the URL so refreshing the page doesn't reopen the modal
            const next = new URLSearchParams(searchParams);
            next.delete("feedback");
            setSearchParams(next, { replace: true });
        }
        // Optional: jump to a tab via /app?tab=chat (used by manifest shortcut)
        const wantedTab = searchParams.get("tab");
        if (wantedTab && ["today", "week", "shopping", "inventory", "chores", "chat"].includes(wantedTab)) {
            setTab(wantedTab);
            const next = new URLSearchParams(searchParams);
            next.delete("tab");
            setSearchParams(next, { replace: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const refresh = useCallback(() => setRefreshKey((k) => k + 1), []);

    const reloadMembers = useCallback(async () => {
        if (!session?.household_id) return;
        try {
            const h = await fetchHousehold(session.household_id);
            setMembers(h.members);
        } catch (e) { console.warn("pages/MiloApp.jsx:", e); }
    }, [session?.household_id]);

    useEffect(() => {
        reloadMembers();
    }, [reloadMembers, refreshKey]);

    useEffect(() => {
        if (householdMenu && myHouseholds.length === 0) {
            fetchMyHouseholds().then(setMyHouseholds).catch(() => {});
        }
    }, [householdMenu, myHouseholds.length]);

    // Close menus on outside click
    useEffect(() => {
        const onDoc = () => {
            setMenuOpen(false);
            setHouseholdMenu(false);
        };
        if (menuOpen || householdMenu) {
            setTimeout(() => document.addEventListener("click", onDoc, { once: true }), 0);
        }
    }, [menuOpen, householdMenu]);

    // Handle Stripe Checkout return (?stripe=success&session_id=…)
    useEffect(() => {
        const stripeStatus = searchParams.get("stripe");
        const sessionId = searchParams.get("session_id");
        if (!stripeStatus) return;
        if (stripeStatus === "success" && sessionId) {
            confirmCheckoutSession(sessionId)
                .then(async (s) => {
                    const success = s.payment_status === "paid" || s.subscription_status === "active" || s.subscription_status === "trialing";
                    if (success) {
                        toast.success(t("billing.toast.success"));
                    } else {
                        toast.success(t("billing.toast.pending"));
                    }
                    // Refresh the cached user so the plan badge flips immediately
                    try { await refreshAuth?.(); } catch { /* noop */ }
                })
                .catch((e) => toast.error(e?.response?.data?.detail || t("billing.toast.confirm_failed")))
                .finally(() => {
                    const sp = new URLSearchParams(searchParams);
                    sp.delete("stripe"); sp.delete("session_id");
                    setSearchParams(sp, { replace: true });
                });
        } else if (stripeStatus === "cancel") {
            toast.info(t("billing.toast.cancelled"));
            const sp = new URLSearchParams(searchParams);
            sp.delete("stripe");
            setSearchParams(sp, { replace: true });
        }
    }, [searchParams, setSearchParams, refreshAuth, t]);

    const handleManageSubscription = async () => {
        setMenuOpen(false);
        try {
            const { url } = await openBillingPortal();
            window.location.assign(url);
        } catch (e) {
            const detail = e?.response?.data?.detail;
            if (detail === "no_customer") {
                toast.info(t("billing.toast.no_customer"));
                nav("/pricing");
            } else {
                toast.error(detail || t("billing.toast.portal_failed"));
            }
        }
    };

    if (!session) {
        nav("/");
        return null;
    }

    const switchHousehold = () => {
        clearActiveHousehold();
        nav("/picker");
    };

    const pickHousehold = (entry) => {
        saveActiveHousehold({
            household_id: entry.household.id,
            household_name: entry.household.name,
            household_code: entry.household.code,
            member_id: entry.member.id,
            member_name: entry.member.name,
        });
        setSession(loadActiveHousehold());
        setHouseholdMenu(false);
        refresh();
    };

    const logout = async () => {
        await logoutApi();
        setUser(null);
        nav("/login");
    };

    return (
        <div className="min-h-screen flex flex-col relative">
            {/* Header */}
            <header className="sticky top-0 z-30 bg-[#faf5ea]/85 backdrop-blur-lg border-b border-[#0d2a1b]/5">
                <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
                    {/* Left: logo + household selector */}
                    <div className="flex items-center gap-2 min-w-0">
                        <img
                            src="/milo-logo.png"
                            alt="Milo"
                            className="h-8 w-auto object-contain shrink-0"
                        />
                        <div className="relative">
                            <button
                                data-testid="household-selector-btn"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setHouseholdMenu((s) => !s);
                                }}
                                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-full hover:bg-white/80 transition-colors min-w-0"
                            >
                                <HomeSimpleDoor className="w-4 h-4 text-[#2F6B43] shrink-0" />
                                <span className="font-semibold text-sm truncate max-w-[8rem] sm:max-w-[14rem]">
                                    {session.household_name}
                                </span>
                                <NavArrowDown className="w-3.5 h-3.5 opacity-60 shrink-0" />
                            </button>
                            <AnimatePresence>
                                {householdMenu && (
                                    <motion.div
                                        initial={{ opacity: 0, y: -4 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0 }}
                                        onClick={(e) => e.stopPropagation()}
                                        className="absolute left-0 top-full mt-2 w-64 bg-white brut-border brut-shadow rounded-2xl p-2 z-50"
                                    >
                                        <div className="text-[10px] uppercase tracking-widest font-bold text-zinc-500 px-3 py-1.5">
                                            Your households
                                        </div>
                                        {myHouseholds.length === 0 ? (
                                            <div className="text-xs text-zinc-500 px-3 py-2">Loading…</div>
                                        ) : (
                                            myHouseholds.map((e) => {
                                                const isActive = e.household.id === session.household_id;
                                                return (
                                                    <button
                                                        key={e.household.id}
                                                        onClick={() => pickHousehold(e)}
                                                        data-testid={`household-option-${e.household.code}`}
                                                        className={`w-full text-left px-3 py-2 rounded-lg text-sm flex items-center gap-2 hover:bg-[#faf5ea] ${isActive ? "bg-[#e8f1e4] font-semibold" : ""}`}
                                                    >
                                                        <HomeSimpleDoor className="w-4 h-4 text-[#2F6B43]" />
                                                        <span className="flex-1 truncate">{e.household.name}</span>
                                                        {isActive && <span className="text-[10px] text-[#2F6B43]">active</span>}
                                                    </button>
                                                );
                                            })
                                        )}
                                        <button
                                            onClick={switchHousehold}
                                            data-testid="manage-households-btn"
                                            className="w-full text-left px-3 py-2 rounded-lg text-sm flex items-center gap-2 hover:bg-[#faf5ea] text-[#2F6B43] font-semibold border-t border-[#0d2a1b]/5 mt-1"
                                        >
                                            <Plus className="w-4 h-4" /> Add or join household
                                        </button>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                    </div>

                    {/* Right: avatars + mic + overflow */}
                    <div className="flex items-center gap-2">
                        {/* Avatar stack */}
                        <div className="hidden sm:flex items-center -space-x-2">
                            {members.slice(0, 3).map((m) => (
                                <div
                                    key={m.id}
                                    className="avatar"
                                    style={{ background: avatarColor(m.name || m.id) }}
                                    title={firstName(m.name)}
                                >
                                    {initials(m.name)}
                                </div>
                            ))}
                            {members.length > 3 && (
                                <div className="avatar bg-white text-[#0d2a1b]">+{members.length - 3}</div>
                            )}
                            <button
                                data-testid="add-member-btn"
                                onClick={() => setInviteOpen(true)}
                                className="avatar bg-white text-[#2F6B43] hover:bg-[#e8f1e4]"
                                aria-label="invite"
                                title="Invite"
                            >
                                <Plus className="w-3.5 h-3.5" />
                            </button>
                        </div>

                        {/* Primary mic */}
                        <button
                            data-testid="open-voice-btn"
                            onClick={() => setVoiceOpen(true)}
                            className="w-10 h-10 rounded-full bg-[#2F6B43] text-white flex items-center justify-center hover:bg-[#255636] transition-colors shadow-[0_4px_12px_rgba(47,107,67,0.3)]"
                            aria-label="voice"
                            title="Voice plan"
                        >
                            <Microphone className="w-4 h-4" />
                        </button>

                        {/* Overflow */}
                        <div className="relative">
                            <button
                                data-testid="overflow-menu-btn"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setMenuOpen((s) => !s);
                                }}
                                className="w-10 h-10 rounded-full bg-white border border-[#0d2a1b]/10 flex items-center justify-center hover:bg-[#faf5ea] transition-colors"
                                aria-label="menu"
                            >
                                <MoreVert className="w-4 h-4" />
                            </button>
                            <AnimatePresence>
                                {menuOpen && (
                                    <motion.div
                                        initial={{ opacity: 0, y: -4 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0 }}
                                        onClick={(e) => e.stopPropagation()}
                                        className="absolute right-0 top-full mt-2 w-56 bg-white brut-border brut-shadow rounded-2xl p-2 z-50"
                                    >
                                        <PlanBadge user={user} t={t} />
                                        <MenuItem testId="menu-invite" label={t("menu.invite_member")} onClick={() => { setInviteOpen(true); setMenuOpen(false); }} icon={Plus} />
                                        <MenuItem testId="menu-refer" label={t("menu.refer_friend")} onClick={() => { setReferOpen(true); setMenuOpen(false); }} icon={Heart} />
                                        <MenuItem
                                            testId="menu-password"
                                            label={user?.has_password ? t("setpw.title") : t("menu.set_password")}
                                            onClick={() => { setPasswordOpen(true); setMenuOpen(false); }}
                                            icon={LockSquare}
                                        />
                                        <MenuItem testId="menu-feedback" label={t("menu.feedback")} onClick={() => { setFeedbackOpen(true); setMenuOpen(false); }} icon={ChatBubbleQuestion} />
                                        <MenuItem testId="menu-telegram" label={t("menu.telegram")} onClick={() => { setTelegramOpen(true); setMenuOpen(false); }} icon={ChatBubble} />
                                        <MenuItem testId="menu-pricing" label={t("menu.pricing")} onClick={() => { nav("/pricing"); setMenuOpen(false); }} icon={Sparks} />
                                        {(user?.plan === "plus" || user?.plan === "premium" || user?.stripe_customer_id) && (
                                            <MenuItem testId="menu-manage-sub" label={t("menu.manage_subscription")} onClick={handleManageSubscription} icon={Sparks} />
                                        )}                                        <div className="h-px bg-[#0d2a1b]/5 my-1" />
                                        <div className="px-3 py-2 text-[11px] uppercase tracking-widest font-bold text-[#5b6d63] flex items-center gap-2">
                                            <Language className="w-3.5 h-3.5" /> {t("menu.language")}
                                        </div>
                                        <div className="px-2 pb-2 flex gap-1">
                                            <button
                                                data-testid="menu-lang-en"
                                                onClick={() => setLang("en")}
                                                className={`flex-1 text-sm font-semibold rounded-lg py-2 transition-colors ${lang === "en" ? "bg-[#2F6B43] text-white" : "bg-[#f5f0e0] text-[#0d2a1b] hover:bg-[#ebe4d0]"}`}
                                            >
                                                English
                                            </button>
                                            <button
                                                data-testid="menu-lang-fr"
                                                onClick={() => setLang("fr")}
                                                className={`flex-1 text-sm font-semibold rounded-lg py-2 transition-colors ${lang === "fr" ? "bg-[#2F6B43] text-white" : "bg-[#f5f0e0] text-[#0d2a1b] hover:bg-[#ebe4d0]"}`}
                                            >
                                                Français
                                            </button>
                                        </div>
                                        {(user?.is_admin || (user?.email || "").toLowerCase() === "eric.degheldere@gmail.com") && (
                                            <MenuItem testId="menu-admin" label={t("menu.admin")} onClick={() => { nav("/admin"); setMenuOpen(false); }} icon={Settings} />
                                        )}
                                        <div className="h-px bg-[#0d2a1b]/5 my-1" />
                                        <MenuItem testId="menu-logout" label={t("menu.sign_out")} onClick={logout} icon={LogOut} tone="danger" />
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                    </div>
                </div>
            </header>

            {/* Body */}
            <main className="flex-1 max-w-5xl w-full mx-auto px-5 sm:px-6 py-5 pb-28">
                <AnimatePresence mode="wait">
                    <motion.div
                        key={tab + refreshKey}
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.2 }}
                    >
                        {tab === "today" && (
                            <TodayTab
                                session={session}
                                members={members}
                                user={user}
                                onRefresh={refresh}
                                onGoto={setTab}
                            />
                        )}
                        {tab === "week" && (
                            <WeekTab session={session} members={members} onRefresh={refresh} />
                        )}
                        {tab === "shopping" && (
                            <ShoppingTab session={session} members={members} onRefresh={refresh} onGoto={setTab} />
                        )}
                        {tab === "inventory" && (
                            <InventoryTab session={session} members={members} onRefresh={refresh} onGoto={setTab} />
                        )}
                        {tab === "chores" && (
                            <ChoresTab session={session} members={members} onRefresh={refresh} />
                        )}
                        {tab === "chat" && (
                            <ChatTab session={session} members={members} onRefresh={refresh} />
                        )}
                    </motion.div>
                </AnimatePresence>
            </main>

            {/* Bottom Nav — slim, modern */}
            <nav className="fixed bottom-0 left-0 right-0 z-40 bg-white/95 backdrop-blur-lg border-t border-[#0d2a1b]/5">
                <div className="max-w-lg mx-auto grid grid-cols-6 px-2 pt-2 pb-[calc(env(safe-area-inset-bottom)+8px)]">
                    {TABS.map((tabDef) => {
                        const Icon = tabDef.icon;
                        const active = tab === tabDef.id;
                        return (
                            <button
                                key={tabDef.id}
                                data-testid={`tab-${tabDef.id}-btn`}
                                onClick={() => setTab(tabDef.id)}
                                className="relative flex flex-col items-center justify-center gap-1 py-1.5 transition-colors"
                            >
                                <Icon className={`w-5 h-5 transition-colors ${active ? "text-[#2F6B43]" : "text-zinc-400"}`} strokeWidth={active ? 2.2 : 1.8} />
                                <span className={`text-[10.5px] font-semibold transition-colors ${active ? "text-[#2F6B43]" : "text-zinc-500"}`}>
                                    {t(tabDef.labelKey)}
                                </span>
                                {active && (
                                    <motion.div
                                        layoutId="tab-underline"
                                        className="absolute -bottom-0.5 h-0.5 w-8 rounded-full bg-[#2F6B43]"
                                    />
                                )}
                            </button>
                        );
                    })}
                </div>
            </nav>

            <AnimatePresence>
                {voiceOpen && (
                    <VoiceOnboarding
                        session={session}
                        onClose={() => setVoiceOpen(false)}
                        onDone={() => {
                            setVoiceOpen(false);
                            refresh();
                            setTab("week");
                            toast.success("Week captured!");
                        }}
                    />
                )}
            </AnimatePresence>

            <InviteDialog
                open={inviteOpen}
                onClose={() => setInviteOpen(false)}
                code={session.household_code}
                householdName={session.household_name}
            />
            <FeedbackDialog
                open={feedbackOpen}
                onClose={() => setFeedbackOpen(false)}
            />
            <ReferFriendDialog open={referOpen} onClose={() => setReferOpen(false)} />
            <SetPasswordDialog
                open={passwordOpen}
                onClose={async () => {
                    setPasswordOpen(false);
                    try {
                        const { data } = await api.get("/auth/me");
                        setUser(data);
                    } catch (e) { console.warn("pages/MiloApp.jsx:", e); }
                }}
                hasPassword={!!user?.has_password}
            />
            <TelegramDialog
                open={telegramOpen}
                onClose={() => setTelegramOpen(false)}
                memberId={session.member_id}
            />
        </div>
    );
}

function MenuItem({ label, onClick, icon: Icon, tone, testId }) {
    return (
        <button
            data-testid={testId}
            onClick={onClick}
            className={`w-full text-left px-3 py-2 rounded-lg text-sm flex items-center gap-2 hover:bg-[#faf5ea] transition-colors ${tone === "danger" ? "text-[#A53F3F]" : ""}`}
        >
            <Icon className="w-4 h-4" /> {label}
        </button>
    );
}

function PlanBadge({ user, t }) {
    const plan = (user?.plan || "free");
    const status = user?.subscription_status || "trial";
    if (!user) return null;
    const planLabel = plan === "free" ? t("plan.free") : plan === "plus" ? t("plan.plus") : t("plan.premium");
    const planStyle = plan === "free"
        ? "bg-[#f5f0e0] text-[#5b6d63]"
        : plan === "plus"
            ? "bg-[#BCE3B6] text-[#1f4d2f]"
            : "bg-[#D4C5E8] text-[#4a2a7a]";
    const statusLabel = status === "trial" ? t("plan.trial") : status === "active" ? t("plan.active") : (status || "").replace(/_/g, " ");
    const showStatus = plan === "free" || status === "active";
    const statusStyle = status === "active"
        ? "bg-[#BCE3B6]/60 text-[#1f4d2f]"
        : "bg-[#F3D469]/40 text-[#7a5c1a]";
    return (
        <div data-testid="menu-plan-badge" className="px-3 py-2 flex items-center justify-between gap-2 border-b border-[#0d2a1b]/5 mb-1">
            <span className="text-[10px] uppercase tracking-widest font-bold text-[#5b6d63]">
                {t("plan.your_plan")}
            </span>
            <span className="flex items-center gap-1">
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${planStyle}`}>{planLabel}</span>
                {showStatus && (
                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full uppercase ${statusStyle}`}>
                        {statusLabel}
                    </span>
                )}
            </span>
        </div>
    );
}

